import React, { useState, useEffect } from 'react';
import { FileText, MessageCircle, Wand2, BarChart3, BookOpen, Edit } from 'lucide-react';
// Removed unused imports: Download, Brain, Target, GraduationCap, jsPDF, html2canvas (Edit kept for header icon)

// Removed unused imports: EssaySamples, TemplateWizard, GeneratedEssay, FlashCardsGenerator, EssayQuiz, PrimaryChinese
import HKDSETexts from './components/HKDSETexts';
import ChineseEssayWritingToolbox from './components/ChineseEssayWritingToolbox';
import DeepSeekChatWindow from './components/DeepSeekChatWindow';
import KnowledgeGraph from './components/KnowledgeGraph';
import SupabaseDebug from './components/SupabaseDebug';
import TextSizeControl from './components/TextSizeControl';
import A2AServerToggle from './components/A2AServerToggle';
import { TextSizeProvider, useTextSize } from './contexts/TextSizeContext';
import chineseDataService from './services/chineseDataService';
// Removed unused data imports: essayTemplates, categories, templateTypes, usefulExpressions

const AppContent = () => {
  const { textSize, setTextSize, tabLabelMode } = useTextSize();
  const [activeMainTab, setActiveMainTab] = useState('writing');
  const [activeSubTab, setActiveSubTab] = useState('knowledge-graph');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [uxFormat, setUxFormat] = useState(null);
  // Removed unused state variables: selectedCategory, searchTerm, selectedEssayForFlashCards, selectedEssayForQuiz, templateData

  // UX Negotiation - Detect context and negotiate UX format
  useEffect(() => {
    const initializeUX = async () => {
      try {
        // Detect user context
        const context = chineseDataService.detectContext();
        console.log('[ChineseDaily] Detected context:', context);
        
        // Negotiate UX format via A2A
        const negotiatedUX = await chineseDataService.negotiateUX(context);
        console.log('[ChineseDaily] Negotiated UX format:', negotiatedUX);
        
        setUxFormat(negotiatedUX);
        
        // Apply UX format settings
        if (negotiatedUX) {
          // Apply font size
          if (negotiatedUX.font_size) {
            const fontSizeMap = { large: 'lg', medium: 'md', small: 'sm' };
            setTextSize(fontSizeMap[negotiatedUX.font_size] || 'md');
          }
          
          // Apply character set preference
          // This will be used by components that support Traditional/Simplified toggle
          // Components can access uxFormat.character_set
        }
      } catch (error) {
        console.error('[ChineseDaily] Error during UX negotiation:', error);
        // Use default UX format on error
        const defaultContext = chineseDataService.detectContext();
        const defaultUX = chineseDataService.getDefaultUXFormat(defaultContext);
        setUxFormat(defaultUX);
      }
    };
    
    initializeUX();
  }, []);

  // Removed unused functions: filteredSamples, handleTemplateChange, autofillFromSample, generateFlashCardsFromEssay, startQuizFromEssay, resetTemplate

  // Removed unused PDF generation function - PDF features are available in ChineseEssayWritingToolbox

  // Main tab configuration - Removed 小學中文 tab
  const mainTabs = [
    { id: 'writing', label: '中文寫作', icon: FileText },
    { id: 'hkdse', label: 'HKDSE 十二篇範文', icon: BookOpen }
  ];

  // Sub tab configuration for Chinese Writing - Only keeping Knowledge Graph and Toolbox
  const writingSubTabs = [
    { id: 'knowledge-graph', label: 'Knowledge Graph', icon: BarChart3 },
    { id: 'toolbox', label: '中文寫作工具箱', icon: Wand2 },
    { id: 'supabase-debug', label: '🔧 Supabase Debug', icon: BarChart3 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Edit className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">中文作文助手</h1>
                <p className="text-sm text-gray-600">運用人工智能幫助創作優秀的中文作文</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            {mainTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveMainTab(tab.id);
                    if (tab.id === 'writing') {
                      setActiveSubTab('knowledge-graph');
                    }
                  }}
                  className={`flex items-center ${tabLabelMode==='icon' ? '' : 'space-x-2'} py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeMainTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tabLabelMode==='icon_text' && <span>{tab.label}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Sub Navigation Tabs - Only show for Chinese Writing */}
      {activeMainTab === 'writing' && (
        <nav className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex space-x-6">
              {writingSubTabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSubTab(tab.id)}
                    className={`flex items-center ${tabLabelMode==='icon' ? '' : 'space-x-2'} py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeSubTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tabLabelMode==='icon_text' && <span>{tab.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <div 
        className={`max-w-7xl mx-auto px-4 py-6 ${
          uxFormat?.font_size === 'large' ? 'text-lg' : 
          uxFormat?.font_size === 'small' ? 'text-sm' : 
          'text-base'
        }`}
        style={{
          maxWidth: uxFormat?.layout === 'cards' ? '100%' : '80rem'
        }}
      >
        {/* Chinese Writing Tab Content - Only Knowledge Graph and Toolbox */}
        {activeMainTab === 'writing' && (
          <>
            {/* Knowledge Graph Sub Tab */}
            {activeSubTab === 'knowledge-graph' && (
              <KnowledgeGraph uxFormat={uxFormat} />
            )}

            {/* Chinese Writing Toolbox Sub Tab */}
            {activeSubTab === 'toolbox' && (
              <ChineseEssayWritingToolbox uxFormat={uxFormat} />
            )}

            {/* Supabase Debug Sub Tab */}
            {activeSubTab === 'supabase-debug' && (
              <SupabaseDebug />
            )}
          </>
        )}

        {/* HKDSE Texts Tab Content */}
        {activeMainTab === 'hkdse' && (
          <HKDSETexts uxFormat={uxFormat} />
        )}
      </div>

      {/* Floating Chat Button */}
      {!isChatOpen && (
        <button
          onClick={() => setIsChatOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-40 hover:scale-110"
          title="AI助手"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* DeepSeek Chat Window */}
      <DeepSeekChatWindow
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        contextData={null}
        initialMode="direct"
      />

      {/* Text Size Control */}
      <TextSizeControl 
        textSize={textSize} 
        onTextSizeChange={setTextSize} 
      />

      {/* A2A Server Toggle (Debug) */}
      {process.env.NODE_ENV === 'development' && <A2AServerToggle />}
    </div>
  );
};

const App = () => {
  return (
    <TextSizeProvider>
      <AppContent />
    </TextSizeProvider>
  );
};

export default App;
